import React, { useEffect, useState } from 'react';
import { listPlans, listAdPackages, ownerUpsertPlan, ownerDeletePlan, ownerUpsertAdPackage, ownerDeleteAdPackage, ownerUpdateSettings } from '../lib/api';
import { toast } from 'sonner';
import { Save, Trash2, Plus, Loader2, DollarSign, HardDrive } from 'lucide-react';

export default function OwnerPricing({ settings, onSettingsChange }) {
  const [plans, setPlans] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([listPlans(), listAdPackages()]);
      setPlans(p); setPkgs(a);
    } catch { toast.error('Failed to load pricing'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const savePlan = async (plan) => {
    try { await ownerUpsertPlan(plan.id, plan); toast.success(`${plan.name} saved`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
  };
  const deletePlan = async (id) => {
    if (!window.confirm(`Delete plan "${id}"? Users on this plan will stay but checkout will fail.`)) return;
    try { await ownerDeletePlan(id); toast.success('Deleted'); load(); }
    catch (err) { toast.error('Failed'); }
  };
  const savePkg = async (p) => {
    try { await ownerUpsertAdPackage(p.id, p); toast.success(`${p.name} saved`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
  };
  const deletePkg = async (id) => {
    if (!window.confirm(`Delete package "${id}"?`)) return;
    try { await ownerDeleteAdPackage(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const saveStorageSettings = async (key, val) => {
    try { const s = await ownerUpdateSettings({ [key]: val }); onSettingsChange?.(s); toast.success('Saved'); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  return (
    <div data-testid="owner-pricing-tab" className="space-y-8">
      <section className="card-shell p-6">
        <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#22d3ee]" /><h3 className="row-title text-white m-0">Subscription plans</h3></div>
        <p className="text-white/60 text-[13px] mt-1">Live prices & storage quotas. Changes apply immediately — no redeploy needed.</p>
        <div className="mt-5 space-y-3">
          {plans.map(p => <PlanRow key={p.id} p={p} onSave={savePlan} onDelete={() => deletePlan(p.id)} />)}
          <AddPlanRow onAdd={savePlan} />
        </div>
      </section>

      <section className="card-shell p-6">
        <div className="flex items-center gap-2"><HardDrive className="w-4 h-4 text-[#ffcf52]" /><h3 className="row-title text-white m-0">Role-based storage defaults</h3></div>
        <p className="text-white/60 text-[13px] mt-1">Users on free plan and admins use these quotas. Owner accounts are always unlimited.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          <NumberSetting label="Free plan (MB)" value={settings?.free_storage_mb ?? 1024} onSave={v => saveStorageSettings('free_storage_mb', v)} testid="free-storage-mb" />
          <NumberSetting label="Admin role (MB)" value={settings?.admin_storage_mb ?? 5120000} onSave={v => saveStorageSettings('admin_storage_mb', v)} testid="admin-storage-mb" />
        </div>
      </section>

      <section className="card-shell p-6">
        <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#ffcf52]" /><h3 className="row-title text-white m-0">Ad marketplace packages</h3></div>
        <p className="text-white/60 text-[13px] mt-1">Flat-rate one-time purchases for ad placements.</p>
        <div className="mt-5 space-y-3">
          {pkgs.map(p => <PkgRow key={p.id} p={p} onSave={savePkg} onDelete={() => deletePkg(p.id)} />)}
          <AddPkgRow onAdd={savePkg} />
        </div>
      </section>
    </div>
  );
}

function PlanRow({ p, onSave, onDelete }) {
  const [f, setF] = useState(p);
  useEffect(() => { setF(p); }, [p]);
  return (
    <div className="grid sm:grid-cols-[1fr_140px_1fr_120px_100px_auto_auto] gap-2 items-center" data-testid={`plan-row-${p.id}`}>
      <input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Name" className="input-field" />
      <input value={f.id} disabled className="input-field opacity-60 font-mono text-[12.5px]" />
      <input value={f.tagline || ''} onChange={e => setF({ ...f, tagline: e.target.value })} placeholder="Tagline" className="input-field" />
      <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">$</span><input type="number" step="0.01" value={f.price} onChange={e => setF({ ...f, price: parseFloat(e.target.value) || 0 })} className="input-field pl-6 w-full" /></div>
      <input type="number" value={f.storage_mb} onChange={e => setF({ ...f, storage_mb: parseInt(e.target.value) || 0 })} className="input-field w-full" title="MB" />
      <button onClick={() => onSave(f)} className="btn-ghost" data-testid={`save-plan-${p.id}`}><Save className="w-4 h-4" /></button>
      <button onClick={onDelete} className="btn-ghost hover:!text-[#ff6b8a]"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

function AddPlanRow({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ id: '', name: '', price: 0, tagline: '', storage_mb: 1024, order: 99 });
  if (!open) return <button onClick={() => setOpen(true)} className="btn-ghost"><Plus className="w-4 h-4" /> Add plan</button>;
  return (
    <div className="grid sm:grid-cols-[1fr_140px_1fr_120px_100px_auto] gap-2 items-center">
      <input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Name" className="input-field" />
      <input value={f.id} onChange={e => setF({ ...f, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })} placeholder="id (lowercase)" className="input-field font-mono" />
      <input value={f.tagline} onChange={e => setF({ ...f, tagline: e.target.value })} placeholder="Tagline" className="input-field" />
      <input type="number" step="0.01" value={f.price} onChange={e => setF({ ...f, price: parseFloat(e.target.value) || 0 })} placeholder="Price $" className="input-field" />
      <input type="number" value={f.storage_mb} onChange={e => setF({ ...f, storage_mb: parseInt(e.target.value) || 0 })} placeholder="MB" className="input-field" />
      <button onClick={() => { if (!f.id || !f.name) return; onAdd(f); setOpen(false); }} className="btn-primary"><Plus className="w-4 h-4" /> Add</button>
    </div>
  );
}

function PkgRow({ p, onSave, onDelete }) {
  const [f, setF] = useState(p);
  useEffect(() => { setF(p); }, [p]);
  return (
    <div className="grid sm:grid-cols-[1fr_140px_140px_100px_100px_auto_auto] gap-2 items-center" data-testid={`pkg-row-${p.id}`}>
      <input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="input-field" />
      <input value={f.id} disabled className="input-field opacity-60 font-mono text-[12.5px]" />
      <select value={f.placement} onChange={e => setF({ ...f, placement: e.target.value })} className="input-field">
        <option value="home_banner">home_banner</option>
        <option value="preroll">preroll</option>
        <option value="row_promoted">row_promoted</option>
        <option value="channel_sponsor">channel_sponsor</option>
        <option value="external_banner">external_banner</option>
      </select>
      <input type="number" value={f.days} onChange={e => setF({ ...f, days: parseInt(e.target.value) || 1 })} className="input-field" title="Days" />
      <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">$</span><input type="number" step="0.01" value={f.price} onChange={e => setF({ ...f, price: parseFloat(e.target.value) || 0 })} className="input-field pl-6 w-full" /></div>
      <button onClick={() => onSave(f)} className="btn-ghost" data-testid={`save-pkg-${p.id}`}><Save className="w-4 h-4" /></button>
      <button onClick={onDelete} className="btn-ghost hover:!text-[#ff6b8a]"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

function AddPkgRow({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ id: '', name: '', placement: 'home_banner', days: 7, price: 0 });
  if (!open) return <button onClick={() => setOpen(true)} className="btn-ghost"><Plus className="w-4 h-4" /> Add package</button>;
  return (
    <div className="grid sm:grid-cols-[1fr_140px_140px_100px_100px_auto] gap-2 items-center">
      <input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Name" className="input-field" />
      <input value={f.id} onChange={e => setF({ ...f, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })} placeholder="id" className="input-field font-mono" />
      <select value={f.placement} onChange={e => setF({ ...f, placement: e.target.value })} className="input-field">
        <option value="home_banner">home_banner</option>
        <option value="preroll">preroll</option>
        <option value="row_promoted">row_promoted</option>
        <option value="channel_sponsor">channel_sponsor</option>
        <option value="external_banner">external_banner</option>
      </select>
      <input type="number" value={f.days} onChange={e => setF({ ...f, days: parseInt(e.target.value) || 1 })} placeholder="Days" className="input-field" />
      <input type="number" step="0.01" value={f.price} onChange={e => setF({ ...f, price: parseFloat(e.target.value) || 0 })} placeholder="Price" className="input-field" />
      <button onClick={() => { if (!f.id || !f.name) return; onAdd(f); setOpen(false); }} className="btn-primary"><Plus className="w-4 h-4" /> Add</button>
    </div>
  );
}

function NumberSetting({ label, value, onSave, testid }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <div className="flex items-end gap-2">
      <label className="block flex-1">
        <span className="text-[12px] text-white/60">{label}</span>
        <input type="number" value={v} onChange={e => setV(parseInt(e.target.value) || 0)} className="input-field w-full mt-1" data-testid={testid} />
      </label>
      <button onClick={() => onSave(v)} className="btn-primary"><Save className="w-4 h-4" /></button>
    </div>
  );
}
